/* ==========================================================================
   Siraat — shared sale countdown driver
   One source of truth for EVERY countdown, on EVERY page: the PDP banner
   (.siraat-pts) and the announcement-bar countdown (.siraat-cd) both use
   [data-siraat-timer] markup with [data-unit="hours|mins|secs"].

   The end time is persisted in localStorage ('siraat_sale_end'), so the
   countdown runs CONTINUOUSLY across page navigations and tabs instead of
   restarting on every page load. Evergreen: at zero it reseeds to 2h 43m.

   2026-09-16 rewrite. The previous version cost ~517 ms of CPU (470 ms of it
   script evaluation) on a mobile homepage load. Three causes, all fixed here:

     1. setInterval(tick, 250) ran 4x/second for a display that only shows
        whole seconds, so three out of every four passes were pure waste.
        Now: one pass per second, self-scheduled onto the wall-clock second
        boundary so it never drifts and never double-fires.
     2. Every pass called localStorage.getItem(). localStorage is synchronous
        and blocks the main thread. Now the end time is read once and held in
        memory; storage is re-read only on a cross-tab 'storage' event or when
        the tab becomes visible again, which are the only two ways it can
        change underneath us.
     3. The loop started at DOMContentLoaded and competed with page load.
        Now one immediate pass paints the correct time, and the repeating loop
        starts after 'load' (on idle where available). While the tab is hidden
        the loop stops entirely and resyncs on return.

   Still no DOM observer, so there is no reaction to unrelated page/app DOM
   churn (that caused the jank). Freshly-rendered timers, e.g. after a size
   selector re-renders its section, are picked up on the next pass.
   ========================================================================== */
(function () {
  'use strict';
  if (window.__siraatSaleLoop) return; /* exactly one loop, even if this asset loads twice */
  window.__siraatSaleLoop = true;

  var KEY = 'siraat_sale_end';
  var DURATION = (2 * 60 * 60 * 1000) + (43 * 60 * 1000); /* 2h 43m */

  var end = 0;          /* in-memory source of truth, so no per-tick storage read */
  var timer = null;     /* pending setTimeout id */

  function pad(n) { return n < 10 ? '0' + n : '' + n; }

  function readStore() {
    try {
      var v = parseInt(localStorage.getItem(KEY), 10);
      if (!isNaN(v) && v) return v;
    } catch (e) {}
    return window.__siraatSaleEnd || 0; /* fallback/cache when localStorage is blocked */
  }
  function writeStore(v) {
    window.__siraatSaleEnd = v; /* always cache so it never re-seeds every pass */
    try { localStorage.setItem(KEY, String(v)); } catch (e) {}
  }

  /* Pull the persisted end into memory. Called at boot, on a cross-tab write,
     and when the tab becomes visible again — never on a normal pass. */
  function resync() {
    end = readStore();
    if (!end || (end - Date.now()) <= 0) {
      end = Date.now() + DURATION;
      writeStore(end);
    }
  }

  function paint() {
    if ((end - Date.now()) <= 0) resync(); /* evergreen reseed */

    var diff = end - Date.now();
    if (diff < 0) diff = 0;
    var h = Math.floor(diff / 3600000); diff -= h * 3600000;
    var m = Math.floor(diff / 60000);   diff -= m * 60000;
    var s = Math.floor(diff / 1000);
    var value = { hours: pad(h), mins: pad(m), secs: pad(s) };

    var nodes = document.querySelectorAll('[data-siraat-timer] [data-unit]');
    for (var i = 0; i < nodes.length; i++) {
      var node = nodes[i];
      var next = value[node.getAttribute('data-unit')];
      if (next !== undefined && node.textContent !== next) node.textContent = next; /* write only on change */
    }
  }

  /* Self-scheduling loop, aligned to the next wall-clock second so the digits
     flip when they should and we never run twice inside one second. */
  function loop() {
    paint();
    if (document.hidden) { timer = null; return; } /* nothing to paint in a hidden tab */
    timer = window.setTimeout(loop, 1000 - (Date.now() % 1000));
  }

  function start() {
    if (timer !== null || document.hidden) return;
    loop();
  }

  function boot() {
    resync();
    paint(); /* correct digits immediately, without starting the loop */

    /* Defer the repeating loop until the page has finished loading, so it never
       competes with the critical path. */
    var begin = function () {
      if (window.requestIdleCallback) {
        window.requestIdleCallback(start, { timeout: 2000 });
      } else {
        window.setTimeout(start, 200);
      }
    };
    if (document.readyState === 'complete') begin();
    else window.addEventListener('load', begin, { once: true });

    document.addEventListener('visibilitychange', function () {
      if (document.hidden) {
        if (timer !== null) { clearTimeout(timer); timer = null; }
      } else {
        resync();
        start();
      }
    });

    window.addEventListener('storage', function (e) {
      if (e.key === KEY) resync(); /* another tab reseeded; stay in sync */
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();
